import React, {useState, useEffect} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import FinanceLayout from 'src/feature/finance/FinanceLayout'
import CardImg from '../finance/assets/card-2.jpg'
import {NumericFormat} from 'react-number-format'
import {BreadCrumb} from 'src/_metronic/layout/components'

enum AppStep {
  OVERVIEW = 0,
  INFO = 1,
  FINANCIALS = 2,
  SELECT_CARD = 3,
  SUCCESS = 4,
}

interface FormData {
  fullName: string
  email: string
  phone: string
  businessName: string
  industry: string
  annualRevenue: string
  monthlySpend: string
  preferredLimit: string
  selectedCard: string
}

const INITIAL_FORM_DATA: FormData = {
  fullName: '',
  email: '',
  phone: '',
  businessName: '',
  industry: '',
  annualRevenue: '',
  monthlySpend: '',
  preferredLimit: '',
  selectedCard: 'Premium Rewards',
}

const INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Professional Services',
  'Manufacturing',
  'Retail & E-commerce',
  'Real Estate',
  'Education',
  'Other',
]

const CARDS = [
  {
    id: 'standard',
    name: 'Standard Business',
    fee: 0,
    features: [
      '1% cashback on all business purchases',
      'Standard fraud protection',
      'Unlimited employee cards',
    ],
    highlight: false,
    image: '/media/finance/business-card-str-2.png',
  },
  {
    id: 'premium',
    name: 'Premium Rewards',
    fee: '95',
    features: [
      '3% cashback on travel and dining',
      'Comprehensive travel insurance',
      '24/7 dedicated business concierge',
      'No foreign transaction fees',
    ],
    highlight: true,
    image: '/media/finance/business-card-str-1.png',
  },
  {
    id: 'elite',
    name: 'Elite Enterprise',
    fee: '495.00',
    features: [
      '5% rewards on software & cloud',
      'Global airport lounge access',
      'Dedicated Enterprise Relationship Manager',
      'Transparent titanium-style design',
    ],
    highlight: false,
    image: '/media/finance/business-card-str-3.png',
  },
]

const Sidebar = ({currentStep, formData}: {currentStep: AppStep; formData: FormData}) => {
  if (currentStep === AppStep.SUCCESS || currentStep === AppStep.OVERVIEW) return null

  return (
    <aside className='col-lg-4'>
      <div className='card border border-gray-200 shadow-sm sticky-top'>
        <div className='card-body pt-5'>
          <div className='py-3'>
            <img src={CardImg} width={100} className='w-100 rounded' />
          </div>
          <div className='d-flex justify-content-between fs-5 py-3'>
            <span className='fw-bold'>Selected Card</span>
            <span className='fw-bold text-primary'>{formData.selectedCard} - My Bank</span>
          </div>
          <div className='d-flex justify-content-between fs-7 mb-3'>
            <span className=''>Program</span>
            <span className='fw-bold'>Global Corporate Select</span>
          </div>
          <div className='d-flex justify-content-between fs-7 mb-3'>
            <span className=''>Est. Processing</span>
            <span className='fw-bold'>2-3 Business Days</span>
          </div>
          <div className='d-flex justify-content-between fs-7 mb-3'>
            <span className=''>Interest Rate</span>
            <span className='fw-bold text-success'>14.9% APR</span>
          </div>
        </div>
      </div>
    </aside>
  )
}

export default function BusinessCardDetailPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [step, setStep] = useState<AppStep>(AppStep.OVERVIEW)
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('mode') === 'apply') {
      setStep(AppStep.INFO)
    }
  }, [location])

  const nextStep = () => setStep((s) => Math.min(s + 1, 4))
  const prevStep = () => setStep((s) => Math.max(s - 1, 0))

  const updateForm = (fields: Partial<FormData>) => {
    setFormData((prev) => ({...prev, ...fields}))
  }

  const renderOverview = () => (
    <>
      <div className='d-flex gap-5 align-items-center'>
        <button
          className='btn btn-primary btn-sm btn-back'
          onClick={() => {
            navigate('/finance/business-card')
          }}
        >
          <i className='fa fa-arrow-left'></i>
        </button>
        <BreadCrumb currentPage='Card Details' />
      </div>
      <div className='mt-5 position-relative w-100'>
        {/* Red Header Section - Full Width */}
        <div
          className='rounded-4 p-10 p-lg-15 text-white mb-8 w-100 position-relative'
          style={{minHeight: '320px', overflow: 'hidden', backgroundColor: '#1e293b'}}
        >
          <div className='d-flex align-items-center gap-3 mb-10'>
            <span
              className='fs-1 fw-bold'
              style={{
                color: 'rgb(253, 234, 218)',
              }}
            >
              Global Corporate
            </span>
            <div className='vr bg-white opacity-50' style={{height: '30px'}}></div>
            <span
              className='fs-2 fw-normal'
              style={{
                color: 'rgb(253, 234, 218)',
              }}
            >
              Premium Rewards
            </span>
          </div>
          <h1
            className='display-6 fw-bold mb-0 lh-base'
            style={{maxWidth: '650px', color: '#FFFFFF !important'}}
          >
            <span style={{color: '#FFFFFF'}}>
              Pay for all your business expenses with accumulated rewards.
            </span>
          </h1>
        </div>

        {/* Floating Card - Positioned Absolutely relative to the full-width wrapper */}
        <div
          className='d-none d-lg-block position-absolute'
          style={{
            width: '420px',
            top: '40px',
            right: '40px',
            zIndex: 5,
          }}
        >
          <div
            className='card border-0 shadow-lg rounded-4 overflow-hidden'
            style={{boxShadow: '0 20px 50px rgba(0,0,0,0.15)'}}
          >
            <div className='card-body p-10 bg-white'>
              <div className='text-center mb-10'>
                <img
                  src={CardImg}
                  alt='Premium Card'
                  className='w-100 rounded-3'
                  style={{boxShadow: '0 10px 30px rgba(0,0,0,0.1)'}}
                />
              </div>

              <button
                className='btn btn-primary w-100 py-5 fs-5 fw-bold mb-10 shadow-sm d-flex align-items-center justify-content-center gap-2'
                onClick={() => setStep(AppStep.INFO)}
              >
                <i className='bi bi-shield-check fs-3'></i>
                Apply Now
              </button>

              <div className='d-flex flex-column gap-6'>
                <div className='d-flex justify-content-between align-items-center fs-6'>
                  <span className='text-muted'>First Year Annual Fee</span>
                  <span className='fw-bold'>$0.00 (Promo)</span>
                </div>
                <div className='separator opacity-10'></div>
                <div className='d-flex justify-content-between align-items-center fs-6'>
                  <span className='text-muted'>Average Earnings</span>
                  <span className='fw-bold'>22,000 Points /mo</span>
                </div>
                <div className='separator opacity-10'></div>
                <div className='d-flex justify-content-between align-items-center fs-6'>
                  <span className='text-muted'>Welcome Reward</span>
                  <span className='fw-bold'>25,000 Points</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='row g-10'>
          <div className='col-lg-8'>
            {/* Quick Features - Using Orange Checkmark Style */}
            <div className='d-flex flex-column gap-5 mb-10'>
              <div
                className='bg-light rounded-4 p-8 d-flex align-items-center gap-5 border-0 shadow-none'
                style={{backgroundColor: '#F3F6F9'}}
              >
                <i className='bi bi-check-lg fs-1 text-success'></i>
                <span className='fs-4 fw-bold'>
                  0% Interest Installment Cash Advance up to $25,000!
                </span>
              </div>
              <div
                className='bg-light rounded-4 p-8 d-flex align-items-center gap-5 border-0 shadow-none'
                style={{backgroundColor: '#F3F6F9'}}
              >
                <i className='bi bi-check-lg fs-1 text-success'></i>
                <span className='fs-4 fw-bold'>Earn up to 22,000 Reward Points per month!</span>
              </div>
            </div>

            {/* Details Section in a Card */}
            <div className='card border border-gray-200 shadow-sm rounded-4'>
              <div className='card-body p-10 p-lg-15'>
                <h2 className='fw-bold text-gray-900 mb-6'>Card Information</h2>
                <p className='mb-8'>
                  Review all the details to find the card that fits your business needs.
                </p>

                {/* Tabs */}
                <div className='d-flex flex-wrap gap-2 mb-10'>
                  {[
                    'Card & Payment',
                    'Transaction Fees',
                    'Insurance & Security',
                    'Rewards & Offers',
                    'About',
                  ].map((tab, idx) => (
                    <span
                      key={tab}
                      className={`px-5 py-1 cursor-pointer ${
                        idx === 0 ? 'border-bottom border-4 border-secondary fw-bold ' : ''
                      }`}
                    >
                      {tab}
                    </span>
                  ))}
                </div>

                {/* Detail Table */}
                <div className='row g-8 gap-8'>
                  <div className='col-md-5'>
                    {[
                      {label: 'Installment Request', value: 'Available'},
                      {label: 'Shopping Interest', value: '3.25% - 4.25%'},
                      {label: 'Overlimit Fee', value: '3.25% - 4.25%'},
                      {label: 'Debt Transfer', value: 'Available'},
                      {label: 'Contactless Payment', value: 'Available'},
                    ].map((item, idx) => (
                      <div key={idx} className='mb-6'>
                        <div className='d-flex justify-content-between align-items-center pb-2 border-bottom border-gray-100'>
                          <span className='fw-semibold text-gray-700'>{item.label}</span>
                          <span className='fw-bold'>{item.value}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className='col-md-5'>
                    {[
                      {label: 'Additional Card', value: 'Available'},
                      {label: 'Bill Payment', value: 'Available'},
                      {label: 'Cash Advance', value: 'Available'},
                      {label: 'Fast Cash', value: 'Available'},
                      {label: 'Additional Card Fee', value: '$45.00'},
                    ].map((item, idx) => (
                      <div key={idx} className='mb-6'>
                        <div className='d-flex justify-content-between align-items-center pb-2 border-bottom border-gray-100'>
                          <span className='fw-semibold text-gray-700'>{item.label}</span>
                          <span className='fw-bold'>{item.value}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )

  return (
    <FinanceLayout
      title={step === AppStep.OVERVIEW ? 'Card Details' : 'Business Credit Card Application'}
      currentPage='Business Credit Card'
    >
      {step === AppStep.OVERVIEW ? (
        renderOverview()
      ) : (
        <div className=' dashboard-apple-page'>
          {step === AppStep.SUCCESS ? (
            <div className='d-flex mt-10'>
              <div className='card border border-gray-200 shadow-sm w-100'>
                <div className='card-body p-8 p-lg-15 text-center'>
                  <div className='w-100px h-100px rounded-circle bg-light-success d-inline-flex align-items-center justify-content-center mb-5'>
                    <i className='bi bi-check-circle-fill text-success fs-1' />
                  </div>
                  <h2 className='fw-bold text-gray-900 mb-3 fs-1'>Application submitted</h2>
                  <p className='fs-5 mb-10 fw-bold'>
                    Your application has been sent and is now under review.
                  </p>

                  <div className='row g-4 text-start mb-10 w-75 mx-auto'>
                    <div className='col-md-4'>
                      <div className='rounded bg-light p-6 h-100'>
                        <div className='text-uppercase fw-bold fs-8 mb-2 text-secondary'>
                          Selected card
                        </div>
                        <div className='fw-bold fs-3 text-gray-900'>
                          {formData.selectedCard} - My Bank
                        </div>
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='rounded bg-light p-6 h-100'>
                        <div className='text-uppercase fw-bold fs-8 mb-2 text-secondary'>
                          Annual fee
                        </div>
                        <div className='fw-bold fs-3 text-gray-900'>
                          ${CARDS.find((c) => c.name === formData.selectedCard)?.fee || 0}
                        </div>
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='rounded bg-light p-6 h-100'>
                        <div className='text-uppercase fw-bold fs-8 mb-2 text-secondary'>
                          Interest rate
                        </div>
                        <div className='fw-bold fs-3 text-primary'>14.9% APR</div>
                      </div>
                    </div>
                  </div>

                  <div className='text-start w-75 mx-auto mb-10'>
                    <h3 className='fw-bold text-gray-900 mb-6'>What happens next</h3>
                    <div className='d-flex flex-column gap-5'>
                      <div className='d-flex align-items-center gap-4'>
                        <div className='w-30px h-30px rounded-circle bg-primary d-flex align-items-center justify-content-center flex-shrink-0'>
                          <span className='text-white fw-bold fs-7'>1</span>
                        </div>
                        <p className='fs-6 mb-0'>
                          Our team will review your application. This usually takes 1–2 business
                          days.
                        </p>
                      </div>
                      <div className='d-flex align-items-center gap-4'>
                        <div className='w-30px h-30px rounded-circle bg-primary d-flex align-items-center justify-content-center flex-shrink-0'>
                          <span className='text-white fw-bold fs-7'>2</span>
                        </div>
                        <p className='fs-6 mb-0'>
                          You may be contacted if additional information is needed.
                        </p>
                      </div>
                      <div className='d-flex align-items-center gap-4'>
                        <div className='w-30px h-30px rounded-circle bg-primary d-flex align-items-center justify-content-center flex-shrink-0'>
                          <span className='text-white fw-bold fs-7'>3</span>
                        </div>
                        <p className='fs-6 mb-0'>
                          Once a decision is made, you’ll see the update here and receive an email
                          notification.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className='d-flex flex-column flex-sm-row gap-4 justify-content-center mt-5'>
                    <button className='btn btn-primary px-10' onClick={() => navigate('/finance')}>
                      Return to finance
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className='mt-10 '>
              <div className='row g-6'>
                <div className='col-lg-8'>
                  {step === AppStep.INFO ? (
                    <div className='card border border-gray-200 shadow-sm'>
                      <div className='card-body p-7 p-lg-8'>
                        <div className='d-flex justify-content-between align-items-center mb-6'>
                          <div>
                            <h3 className='dashboard-apple-eyebrow text-uppercase fw-semibold mb-1'>
                              Step 1 of 2
                            </h3>
                            <span className='fs-3 fw-bold text-gray-900 mb-0'>
                              Business contact information
                            </span>
                          </div>
                        </div>
                        <div className='row g-5'>
                          <div className='col-md-6'>
                            <label className='form-label fw-bold'>Full name</label>
                            <input
                              type='text'
                              className='form-control form-control-solid'
                              placeholder='Jane Doe'
                              value={formData.fullName}
                              onChange={(e) => updateForm({fullName: e.target.value})}
                            />
                          </div>
                          <div className='col-md-6'>
                            <label className='form-label fw-bold'>Business legal name</label>
                            <input
                              type='text'
                              className='form-control form-control-solid'
                              placeholder='Acme LLC'
                              value={formData.businessName}
                              onChange={(e) => updateForm({businessName: e.target.value})}
                            />
                          </div>
                          <div className='col-md-6'>
                            <label className='form-label fw-bold'>Email address</label>
                            <input
                              type='email'
                              className='form-control form-control-solid'
                              placeholder='jane@company.com'
                              value={formData.email}
                              onChange={(e) => updateForm({email: e.target.value})}
                            />
                          </div>
                          <div className='col-md-6'>
                            <label className='form-label fw-bold'>Phone number</label>
                            <input
                              type='text'
                              className='form-control form-control-solid'
                              placeholder='(555) 000-0000'
                              value={formData.phone}
                              onChange={(e) => updateForm({phone: e.target.value})}
                            />
                          </div>
                        </div>
                        <div className='d-flex justify-content-start align-items-center mt-10 gap-5'>
                          <span
                            className='d-inline-flex cursor-pointer align-items-center gap-2 text-primary fw-bold fs-7 text-secondary'
                            onClick={() => navigate('/finance/business-card')}
                          >
                            <i className='bi bi-arrow-left text-secondary'></i>
                            Back to offers
                          </span>

                          <button className='btn btn-primary' onClick={nextStep}>
                            Continue
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {step === AppStep.FINANCIALS ? (
                    <div className='card border border-gray-200 shadow-sm'>
                      <div className='card-body p-7 p-lg-8'>
                        <div className='d-flex justify-content-between align-items-center mb-6'>
                          <div>
                            <h3 className='dashboard-apple-eyebrow text-uppercase fw-semibold mb-1'>
                              Step 2 of 2
                            </h3>
                            <span className='fs-3 fw-bold text-gray-900 mb-0'>
                              Operating and financial details
                            </span>
                          </div>
                        </div>
                        <div className='row g-5'>
                          <div className='col-md-6'>
                            <label className='form-label fw-bold'>Industry</label>
                            <select
                              className='form-select form-select-solid'
                              value={formData.industry}
                              onChange={(e) => updateForm({industry: e.target.value})}
                            >
                              <option value=''>Select an industry</option>
                              {INDUSTRIES.map((ind) => (
                                <option key={ind} value={ind}>
                                  {ind}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className='col-md-6'>
                            <label className='form-label fw-bold'>Annual revenue</label>
                            <div className='input-group input-group-solid'>
                              <span className='input-group-text'>$</span>
                              <NumericFormat
                                className='form-control'
                                decimalScale={2}
                                thousandSeparator
                                placeholder='0.00'
                                value={formData.annualRevenue}
                                onChange={(e) => updateForm({annualRevenue: e.target.value})}
                              />
                            </div>
                          </div>
                          <div className='col-md-6'>
                            <label className='form-label fw-bold'>Est. Monthly Card Spend</label>
                            <div className='input-group input-group-solid'>
                              <span className='input-group-text'>$</span>
                              <NumericFormat
                                className='form-control'
                                decimalScale={2}
                                thousandSeparator
                                placeholder='0.00'
                                value={formData.monthlySpend}
                                onChange={(e) => updateForm({monthlySpend: e.target.value})}
                              />
                            </div>
                          </div>
                          <div className='col-md-6'>
                            <label className='form-label fw-bold'>Preferred Credit Limit</label>
                            <div className='input-group input-group-solid'>
                              <span className='input-group-text'>$</span>
                              <NumericFormat
                                className='form-control'
                                decimalScale={2}
                                thousandSeparator
                                placeholder='0.00'
                                value={formData.preferredLimit}
                                onChange={(e) => updateForm({preferredLimit: e.target.value})}
                              />
                            </div>
                          </div>
                        </div>
                        <div className='d-flex justify-content-start align-items-center mt-10 gap-5'>
                          <span
                            className='d-inline-flex cursor-pointer align-items-center gap-2 text-primary fw-bold fs-7 text-secondary'
                            onClick={prevStep}
                          >
                            <i className='bi bi-arrow-left text-secondary'></i>
                            Previous
                          </span>
                          <button
                            className='btn btn-primary'
                            onClick={() => setStep(AppStep.SUCCESS)}
                          >
                            Submit Application
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>

                <Sidebar currentStep={step} formData={formData} />
              </div>
            </div>
          )}
        </div>
      )}
    </FinanceLayout>
  )
}
